import React, { useState, useRef, useEffect } from 'react'
import '../../assets/styles/myaccount.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import commonService from '../../services/otherServices';
import Popup from '../../components/Common/Error';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface FormData {
    first_name: string;
    last_name: string;
    phone_number: string;
    job_title?: string;
    linkedin_id?: string;
    facebook_id?: string;
    twitter_id?: string;
    instagram_id?: string;
}

interface FormErrors {
    first_name?: string;
    last_name?: string;
    phone_number?: string;
    [key: string]: string | undefined;
}

const setLocalStorageWithEvent = (key: string, value: string) => {
    localStorage.setItem(key, value);
    // Dispatch custom event for same tab
    window.dispatchEvent(new CustomEvent('localStorageChange', {
        detail: { key, value }
    }));
};

const MyAccount = () => {
    const [showUploadeImage, setShowUploadeImage] = useState(false);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isLoading, setIsLoading] = useState(false)
    const [errorMsg, setErrorMsg] = useState("")
    const [profilePic, setProfilePic] = useState<string>('/images/huge-profile.png');
    const [imagePreview, setImagePreview] = useState<string>('');
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const initialFormRef = useRef<FormData | null>(null);
    const initialProfilePicRef = useRef<string>('');

    // Form state
    const [formData, setFormData] = useState<FormData>({
        first_name: '',
        last_name: '',
        phone_number: '',
        job_title: '',
        linkedin_id: '',
        facebook_id: '',
        twitter_id: '',
        instagram_id: ''
    });

    // Form errors state
    const [formErrors, setFormErrors] = useState<FormErrors>({});

    // Email state (read-only)
    const email = localStorage.getItem('email') || ""; // Replace with actual user email

    useEffect(() => {
        const initialData = {
            first_name: localStorage.getItem('name') || "",
            last_name: localStorage.getItem('last_name') || "",
            phone_number: localStorage.getItem('phone_number') || "",
            job_title: localStorage.getItem('job_title') || "",
            linkedin_id: localStorage.getItem('linkedin_id') || "",
            facebook_id: localStorage.getItem('facebook_id') || "",
            twitter_id: localStorage.getItem('twitter_id') || "",
            instagram_id: localStorage.getItem('instagram_id') || ""
        };

        setFormData(initialData);

        // 🔒 Save snapshot
        initialFormRef.current = initialData;
        initialProfilePicRef.current = localStorage.getItem('profile_pic') || '';

    }, []);

    const hasFormChanged = () => {
        if (!initialFormRef.current) return false;

        return Object.keys(formData).some((key) => {
            const k = key as keyof FormData;
            return (formData[k] || '') !== (initialFormRef.current?.[k] || '');
        });
    };

    useEffect(() => {
        // Load profile picture from localStorage when component mounts
        const savedProfilePic = localStorage.getItem('profile_pic');
        if (savedProfilePic && savedProfilePic.trim() !== '') {
            setProfilePic(savedProfilePic);
        }
    }, []);
    // Initialize form data (you can fetch from API)
    useEffect(() => {
        // TODO: Fetch user data from API and populate form
        // For now, setting default values
        setFormData({
            first_name: localStorage.getItem('name') || "",
            last_name: localStorage.getItem('last_name') || "",
            phone_number: localStorage.getItem('phone_number') || "",
            job_title: localStorage.getItem('job_title') || "",
            linkedin_id: localStorage.getItem('linkedin_id') || "",
            facebook_id: localStorage.getItem('facebook_id') || "",
            twitter_id: localStorage.getItem('twitter_id') || "",
            instagram_id: localStorage.getItem('instagram_id') || ""
        });
    }, []);

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Clear error for this field when user starts typing
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: ''
            });
        }
    };

    // Validation function
    const validateForm = (): boolean => {
        const errors: FormErrors = {};

        // First name validation
        if (!formData.first_name.trim()) {
            errors.first_name = 'First name is required';
        } else if (formData.first_name.length > 255) {
            errors.first_name = 'First name cannot exceed 255 characters';
        }

        // Last name validation
        if (!formData.last_name.trim()) {
            errors.last_name = 'Last name is required';
        } else if (formData.last_name.length > 255) {
            errors.last_name = 'Last name cannot exceed 255 characters';
        }

        // Phone number validation
        if (!formData.phone_number.trim()) {
            errors.phone_number = 'Phone number is required';
        } else if (formData.phone_number.length < 5 || formData.phone_number.length > 20) {
            errors.phone_number = 'Phone number must be between 5 and 20 characters';
        }

        // Optional fields length validation
        if (formData.job_title && formData.job_title.length > 255) {
            errors.job_title = 'Job title cannot exceed 255 characters';
        }

        if (formData.linkedin_id && formData.linkedin_id.length > 255) {
            errors.linkedin_id = 'LinkedIn ID cannot exceed 255 characters';
        }

        if (formData.facebook_id && formData.facebook_id.length > 255) {
            errors.facebook_id = 'Facebook ID cannot exceed 255 characters';
        }

        if (formData.twitter_id && formData.twitter_id.length > 255) {
            errors.twitter_id = 'Twitter ID cannot exceed 255 characters';
        }

        if (formData.instagram_id && formData.instagram_id.length > 255) {
            errors.instagram_id = 'Instagram ID cannot exceed 255 characters';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Show success toast
    const showSuccessToast = (message: string) => {
        toast.success(message, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
                backgroundColor: '#4CAF50', // Green background
                color: 'white'
            }
        });
    };

    // Show error toast
    const showErrorToast = (message: string) => {
        toast.error(message, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
                backgroundColor: '#f44336', // Red background
                color: 'white'
            }
        });
    };

    const submitUpdate = async () => {

        if (!hasFormChanged()) {
            showErrorToast('No changes detected');
            return;
        }
        // Validate form first
        if (!validateForm()) {
            showErrorToast('Please fix the errors in the form');
            return;
        }

        try {
            setIsLoading(true);

            // Prepare payload according to UserProfileUpdateRequest
            const payload = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone_number: formData.phone_number,
                job_title: formData.job_title || null,
                linkedin_id: formData.linkedin_id || null,
                facebook_id: formData.facebook_id || null,
                twitter_id: formData.twitter_id || null,
                instagram_id: formData.instagram_id || null
            };

            const response = await commonService.profileUpdate(payload);
            if (response && response?.data?.status === false) {
                setErrorMsg('Failed to update profile');
                showErrorToast('Failed to update profile');
                return;
            }
            initialFormRef.current = { ...formData };
            // To these:
            setLocalStorageWithEvent('name', response?.data?.first_name || "");
            setLocalStorageWithEvent('last_name', response?.data?.last_name || "");
            setLocalStorageWithEvent('phone_number', response?.data?.phone_number || "");
            setLocalStorageWithEvent('job_title', response?.data?.job_title || "");
            setLocalStorageWithEvent('linkedin_id', response?.data?.linkedin_id || "");
            setLocalStorageWithEvent('facebook_id', response?.data?.facebook_id || "");
            setLocalStorageWithEvent('twitter_id', response?.data?.twitter_id || "");
            setLocalStorageWithEvent('instagram_id', response?.data?.instagram_id || "");
            // Show success toast
            // showSuccessToast('Profile updated successfully!');
            setShowSuccessPopup(true)

        } catch (err) {
            let errorMessage = 'Something went wrong';
            if (err instanceof Error) {
                errorMessage = err.message;
            } else if (typeof err === 'string') {
                errorMessage = err;
            }
            setErrorMsg(errorMessage);
            showErrorToast(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            showErrorToast('File size must be less than 5MB');
            return;
        }

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!validTypes.includes(file.type)) {
            showErrorToast('Only JPG and PNG files are allowed');
            return;
        }

        setSelectedImage(file);

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
    };

    const icons = {
        delete: {
            active: "/images/active-delete.png",
            inactive: "/images/inactive-delete.png",
        },
        save: {
            active: "/images/save.png",
            inactive: "/images/save.png", // use your inactive save icon
        },
    };

    const profilePictureUpdate = async () => {
        if (!selectedImage) return;

        try {
            setIsImageLoading(true);

            // Convert image to base64
            const reader = new FileReader();
            reader.readAsDataURL(selectedImage);

            reader.onload = async () => {
                const base64String = reader.result as string;
                const payload = {
                    profile_pic: base64String
                }
                try {
                    const response = await commonService.updatePicture(payload);

                    if (response && response?.data?.status === false) {
                        showErrorToast('Failed to update profile picture');
                        return;
                    }

                    setLocalStorageWithEvent('profile_pic', base64String);
                    setProfilePic(base64String);
                    setShowSuccessMessage(true);
                    setSelectedImage(null);
                    setImagePreview('');
                    setTimeout(() => {
                        setShowSuccessMessage(false);
                        setShowUploadeImage(false);

                    }, 5000);
                } catch (err) {
                    setShowErrorMessage(true);
                    setTimeout(() => {
                        setShowErrorMessage(false);
                    }, 5000);

                } finally {
                    setIsImageLoading(false);
                }
            };

            reader.onerror = () => {
                showErrorToast('Error reading image file');
                setIsImageLoading(false);
            };

        } catch (err) {
            showErrorToast('Failed to update profile picture');
            setIsImageLoading(false);
        }
    };

    const removeProfilePicture = async () => {
        try {
            setIsImageLoading(true);

            // If there's a selected image, just clear the selection (user hasn't saved it yet)
            if (selectedImage) {
                // Clear the selected image and preview
                setSelectedImage(null);
                setImagePreview('');

                // Cleanup object URL
                if (imagePreview) {
                    URL.revokeObjectURL(imagePreview);
                    setImagePreview('');
                }

                showSuccessToast('Image selection cleared');
            } else {
                // If no selected image but we want to remove existing profile picture
                // Call API to remove profile picture
                const response = await commonService.removeProfilePicture();

                if (response && response?.data?.status === false) {
                    showErrorToast('Failed to remove profile picture');
                    return;
                }

                // Clear from localStorage
                localStorage.removeItem('profile_pic');
                setProfilePic('/images/huge-profile.png');

                // Close popup
                setShowUploadeImage(false);

                showSuccessToast('Profile picture removed successfully!');
            }

        } catch (err) {
            showErrorToast('Failed to remove profile picture');
        } finally {
            setIsImageLoading(false);
        }
    };

    const handleClosePopup = () => {
        // Cleanup object URL if exists
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }

        // Reset state
        setSelectedImage(null);
        setImagePreview('');
        setShowUploadeImage(false);
    };

    useEffect(() => {
        return () => {
            // Cleanup object URLs to prevent memory leaks
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    return (
        <>
            <div className="container">
                {/* <ToastContainer /> */}
                <h4 className="my-account" style={{ marginTop: "6rem" }}>My Account</h4>

                <div className="my-account-box">
                    <div className="d-flex flex-row justify-content-between">
                        <div>
                            <p className="profile-information">
                                Profile Information
                            </p>
                            <p className="details-keep">Keep your account details up to date to ensure smooth <br /> communication and access to all DataCaptive services.</p>

                        </div>
                        <div className="profile-image-wrapper">
                            <img src={imagePreview || profilePic} alt="" className="profile-main" />
                            <img src="/images/uploade-profile.png" alt="" onClick={() => setShowUploadeImage(true)} className="profile-upload-icon" />
                        </div>
                    </div>

                    {showUploadeImage && (
                        <div className="upload-popup-overlay">
                            <div className="upload-popup">
                                <div className="uploade-popup-white">
                                    <div className="d-flex flex-row justify-content-end">
                                        <div>
                                            <img src="/images/cancl.png" style={{ cursor: "pointer" }} onClick={() => handleClosePopup()} alt="" />
                                        </div>
                                    </div>

                                    <p className="uploaded-profile-header">Update Your Profile Picture</p>

                                    <div
                                        className="dashed-box"
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <div className="d-flex flex-row justify-content-center">
                                            <img
                                                src={
                                                    imagePreview || profilePic
                                                }
                                                alt=""
                                                className="uploade-image-size"
                                            />
                                        </div>

                                        <p className="drag mt-4">
                                            Drag & drop or <span className="drop">click to upload</span>
                                        </p>
                                        <p className="jpg">(JPG or PNG, up to 5MB)</p>
                                    </div>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/png, image/jpeg"
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                </div>
                                {showSuccessMessage && (<div style={{ fontSize: "13px", color: "green", textAlign: "center", marginTop: "5px" }}>Profile picture updated successfully!</div>)}
                                {showErrorMessage && (<div style={{ fontSize: "13px", color: "red", textAlign: "center", marginTop: "5px" }}>Failed to update profile picture</div>)}

                                <div className="d-flex flex-row justify-content-start">
                                    {/* Remove Picture */}
                                    <button
                                        style={{width:"200px"}}
                                        type="button"
                                        className={!selectedImage || isImageLoading ? "delete-profile-disable" : "delete-profile"}
                                        disabled={!selectedImage || isImageLoading}
                                        onClick={removeProfilePicture}
                                    >
                                        <img
                                            src={
                                                !selectedImage || isImageLoading
                                                    ? icons.delete.inactive
                                                    : icons.delete.active
                                            }
                                            width="12"
                                            alt="delete"
                                        />
                                        &nbsp; Remove Picture

                                    </button>

                                    {/* Save Profile */}
                                    <button
                                        type="button"
                                        style={{width:"180px"}}                                        
                                        className={(!selectedImage || isImageLoading) ? "save-profile-disable" : "save-profile"}
                                        disabled={!selectedImage || isImageLoading}
                                        onClick={profilePictureUpdate}
                                    >
                                        {isImageLoading ? (
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                        ) : (
                                            <>
                                                <img
                                                    src={
                                                        selectedImage
                                                            ? icons.save.active
                                                            : icons.save.inactive
                                                    }
                                                    alt="save"
                                                />
                                                &nbsp; Save Changes
                                            </>
                                        )}
                                    </button>
                                </div>

                            </div>
                        </div>
                    )}


                    <div className="row mr-auto">
                        <div className="col-md-6">
                            <label htmlFor="first_name" className="my-name">
                                First Name *
                            </label>
                            <input
                                type="text"
                                className={`my-input ${formErrors.first_name ? 'input-error' : ''}`}
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                maxLength={255}
                            />
                            {formErrors.first_name && <span className="error-text"> {formErrors.first_name}</span>}
                        </div>

                        <div className="col-md-6">
                            <label htmlFor="last_name" className="my-name">
                                Last Name *
                            </label>
                            <input
                                type="text"
                                className={`my-input ${formErrors.last_name ? 'input-error' : ''}`}
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                maxLength={255}
                            />
                            {formErrors.last_name && <span className="error-text"> {formErrors.last_name}</span>}
                        </div>

                        <div className="col-md-6 mt-2">
                            <label htmlFor="job_title" className="my-name">
                                Job Title (Optional)
                                {formErrors.job_title && <span className="error-text"> {formErrors.job_title}</span>}
                            </label>
                            <input
                                type="text"
                                className={`my-input ${formErrors.job_title ? 'input-error' : ''}`}
                                name="job_title"
                                value={formData.job_title}
                                onChange={handleInputChange}
                                maxLength={255}
                            />
                        </div>

                        <div className="col-md-6 mt-2">
                            <label htmlFor="email" className="my-name">Business Email Address</label>
                            <input
                                type="text"
                                className="my-input"
                                value={email}
                                readOnly
                                disabled
                                style={{ cursor: 'not-allowed', fontWeight:"bold" }}
                            />
                            {/* <small className="text-muted">Email cannot be changed</small> */}
                        </div>

                        <div className="col-md-6 mt-2">
                            <label htmlFor="phone_number" className="my-name">
                                Phone Number *
                            </label>
                            <input
                                type="tel"
                                className={`my-input ${formErrors.phone_number ? 'input-error' : ''}`}
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleInputChange}
                                maxLength={20}
                                minLength={5}
                            />
                            {formErrors.phone_number && <span className="error-text"> {formErrors.phone_number}</span>}
                        </div>
                    </div>
                    <div className="row mr-auto mt-4">
                        <p className="social-media">Social Media Profiles (Optional)</p>
                        <p className="details-keep">Connect your professional accounts to help us personalize your experience.</p>

                        <div className="col-md-6">
                            <div className="bottom-input-group">
                                <label className="bottom-input-label">
                                    LinkedIn Profile URL
                                    {formErrors.linkedin_id && <span className="error-text"> {formErrors.linkedin_id}</span>}
                                </label>

                                <div className="bottom-input-wrapper">
                                    <input
                                        type="text"
                                        className={`bottom-input ${formErrors.linkedin_id ? 'input-error' : ''}`}
                                        placeholder="LinkedIn ID"
                                        name="linkedin_id"
                                        value={formData.linkedin_id}
                                        onChange={handleInputChange}
                                        maxLength={255}
                                    />

                                    <img
                                        src="/images/my-linkedin.png"
                                        alt="icon"
                                        className="bottom-input-icon"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="bottom-input-group">
                                <label className="bottom-input-label">
                                    Twitter Handle
                                    {formErrors.twitter_id && <span className="error-text"> {formErrors.twitter_id}</span>}
                                </label>

                                <div className="bottom-input-wrapper">
                                    <input
                                        type="text"
                                        className={`bottom-input ${formErrors.twitter_id ? 'input-error' : ''}`}
                                        placeholder="Twitter ID"
                                        name="twitter_id"
                                        value={formData.twitter_id}
                                        onChange={handleInputChange}
                                        maxLength={255}
                                    />

                                    <img
                                        src="/images/my-twitter.png"
                                        alt="icon"
                                        className="bottom-input-icon"
                                    />
                                </div>
                            </div>
                        </div>


                        <div className="col-md-6 mt-4">
                            <div className="bottom-input-group">
                                <label className="bottom-input-label">
                                    Facebook Page / ID
                                    {formErrors.facebook_id && <span className="error-text"> {formErrors.facebook_id}</span>}
                                </label>

                                <div className="bottom-input-wrapper">
                                    <input
                                        type="text"
                                        className={`bottom-input ${formErrors.facebook_id ? 'input-error' : ''}`}
                                        placeholder="Facebook ID"
                                        name="facebook_id"
                                        value={formData.facebook_id}
                                        onChange={handleInputChange}
                                        maxLength={255}
                                    />

                                    <img
                                        src="/images/my-facebook.png"
                                        alt="icon"
                                        className="bottom-input-icon"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6 mt-4">
                            <div className="bottom-input-group">
                                <label className="bottom-input-label">
                                    Instagram Handle
                                    {formErrors.instagram_id && <span className="error-text"> {formErrors.instagram_id}</span>}
                                </label>

                                <div className="bottom-input-wrapper">
                                    <input
                                        type="text"
                                        className={`bottom-input ${formErrors.instagram_id ? 'input-error' : ''}`}
                                        placeholder="Instagram ID"
                                        name="instagram_id"
                                        value={formData.instagram_id}
                                        onChange={handleInputChange}
                                        maxLength={255}
                                    />

                                    <img
                                        src="/images/my-insta.png"
                                        alt="icon"
                                        className="bottom-input-icon"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <button className="save-and-update"
                            disabled={isLoading || !hasFormChanged()}
                            onClick={submitUpdate}>
                            {isLoading ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    Updating...
                                </>
                            ) : (
                                "Save & Update Profile"
                            )}

                        </button>
                    </div>

                </div>
            </div>

            {showSuccessPopup && (
                <div className="submit-popup-overlay">
                    <div className="submit-popup">
                        {/* Close icon */}
                        <div className="d-flex justify-content-end">
                            <img src="/images/close-active.png" alt="" className="submit-popup-close"
                                onClick={() => setShowSuccessPopup(false)}
                            />
                        </div>

                        <div className="d-flex justify-content-center">
                            <img src="/images/hand-shak.png" alt="" />
                        </div>

                        {/* Content */}
                        <p className="submit-popup-title">Profile Updated Successfully!</p>
                        <p className="submit-popup-text mt-3" style={{ fontWeight: 100, textAlign: "center" }}>
                            Your information has been saved and synced with<br /> your DataCaptive account.
                        </p>
                    </div>
                </div>
            )}
            <Popup
                isOpen={!!errorMsg}
                onClose={() => setErrorMsg("")}
                title={errorMsg}
                iconSrc="/images/grey-cancel-cta.png"
            />
        </>
    )
}

export default MyAccount;